import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { ComingSoonProduct, ImageRef, ProductCatalogEntry, UserProfile } from '../types/api';
import InputBar from './InputBar';
import TopoBackground from './TopoBackground';
import styles from './WelcomeScreen.module.css';

interface Props {
  onSend: (
    query: string,
    product: string | null,
    attachments?: ImageRef[],
    draftSessionId?: string,
  ) => void;
  isExiting?: boolean;
  user?: UserProfile | null;
  attachmentsEnabled?: boolean;
  products: ProductCatalogEntry[];
  comingSoon: ComingSoonProduct[];
  catalogLoading: boolean;
  catalogError: boolean;
  selectedSlug: string | null;
  onSelectProduct: (slug: string, version?: string) => void;
  selectedVersion: string | null;
  /** Pinned to the bottom of the screen, below the centred content (the What's new pill). */
  notice?: ReactNode;
}

// Icons for the backend-driven starter categories, keyed by category id. Any
// category id without an entry here falls back to FALLBACK_CATEGORY_ICON.
const CATEGORY_ICONS: Record<string, ReactNode> = {
  start: (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.2" />
      <polygon points="8 4.6 9.8 8 8 11.4 6.2 8" />
    </svg>
  ),
  build: (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </svg>
  ),
  results: (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    >
      <polygon points="2 4 6 2 10 4 14 2 14 12 10 14 6 12 2 14" />
      <line x1="6" y1="2" x2="6" y2="12" />
      <line x1="10" y1="4" x2="10" y2="14" />
    </svg>
  ),
  troubleshooting: (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2.4" />
      <line x1="3.8" y1="3.8" x2="6.3" y2="6.3" />
      <line x1="9.7" y1="9.7" x2="12.2" y2="12.2" />
      <line x1="12.2" y1="3.8" x2="9.7" y2="6.3" />
      <line x1="6.3" y1="9.7" x2="3.8" y2="12.2" />
    </svg>
  ),
};

const FALLBACK_CATEGORY_ICON = (
  <svg
    aria-hidden="true"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="8" cy="8" r="6.2" />
    <path d="M6.1 6.3a1.9 1.9 0 1 1 3.1 1.5c-.6.5-1.1.8-1.1 1.7" />
    <circle cx="8" cy="11.6" r="0.4" fill="currentColor" stroke="none" />
  </svg>
);

function getCategoryIcon(id: string): ReactNode {
  return CATEGORY_ICONS[id] ?? FALLBACK_CATEGORY_ICON;
}

export default function WelcomeScreen({
  onSend,
  isExiting = false,
  user,
  attachmentsEnabled = true,
  products,
  comingSoon,
  catalogLoading,
  catalogError,
  selectedSlug,
  onSelectProduct,
  selectedVersion,
  notice,
}: Props) {
  const firstName = user?.name?.split(' ')[0];
  const [activeId, setActiveId] = useState<string | null>(null);
  // Last opened category — kept so the panel still shows its content while it
  // collapses, instead of blanking out mid-animation. Null until the catalog
  // resolves and seeds it with the selected product's first category.
  const [lastId, setLastId] = useState<string | null>(null);
  const viewRef = useRef<HTMLDivElement | null>(null);

  const activeProduct = products.find((p) => p.slug === selectedSlug) ?? null;
  const categories = activeProduct?.starters ?? [];

  // Seed lastId once the catalog + selection resolve, so the panel has content
  // the first time a chip is opened. Only runs while lastId is still null, so
  // it never fights a later user selection.
  useEffect(() => {
    if (lastId === null && categories.length > 0) {
      setLastId(categories[0].id);
    }
  }, [categories, lastId]);

  const open = activeId !== null;
  const displayCategory =
    categories.find((c) => c.id === (activeId ?? lastId)) ?? categories[0] ?? null;

  const handleChip = (id: string) => {
    setActiveId((prev) => (prev === id ? null : id));
    setLastId(id);
  };

  const handleSend = (
    query: string,
    pickedProduct: string,
    attachments: ImageRef[],
    draftSessionId: string,
  ) => {
    onSend(query, pickedProduct, attachments, draftSessionId);
  };

  return (
    <div className={styles.view} ref={viewRef}>
      <TopoBackground containerRef={viewRef} />
      <div className={styles.glow} />
      <div className={`${styles.content}${isExiting ? ` ${styles.exiting}` : ''}`}>
        <h1 className={styles.heading}>
          {firstName ? `Hi ${firstName}, how can we help you today?` : 'How can we help you today?'}
        </h1>
        <div className={styles.inputWrap}>
          <InputBar
            sessionId={null}
            onSend={handleSend}
            placeholder={activeProduct?.placeholder ?? 'Ask a question...'}
            attachmentsEnabled={attachmentsEnabled}
            products={products}
            comingSoon={comingSoon}
            catalogLoading={catalogLoading}
            selectedSlug={selectedSlug}
            onSelectProduct={onSelectProduct}
            selectedVersion={selectedVersion}
          />
        </div>
        {categories.length > 0 && displayCategory && (
          <div className={styles.suggest}>
            <div className={styles.chips}>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`${styles.chip} ${activeId === c.id ? styles.chipActive : ''}`}
                  onClick={() => handleChip(c.id)}
                  aria-expanded={activeId === c.id}
                  aria-controls="welcome-question-panel"
                >
                  <span className={styles.chipIcon} aria-hidden="true">
                    {getCategoryIcon(c.id)}
                  </span>
                  {c.label}
                </button>
              ))}
            </div>
            <div className={`${styles.panelWrap} ${open ? styles.panelWrapOpen : ''}`}>
              <div className={styles.panelClip}>
                <div className={styles.panel} id="welcome-question-panel">
                  <div className={styles.panelHeader}>
                    <span className={styles.panelTitle}>{displayCategory.label}</span>
                  </div>
                  <ul className={styles.questionList}>
                    {displayCategory.questions.map((q) => (
                      <li key={q}>
                        <button
                          type="button"
                          className={styles.question}
                          onClick={() => selectedSlug && onSend(q, selectedSlug)}
                        >
                          <span>{q}</span>
                          <svg
                            className={styles.questionArrow}
                            aria-hidden="true"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="3" y1="8" x2="12" y2="8" />
                            <polyline points="8 4 12 8 8 12" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        {categories.length === 0 && catalogError && (
          <p className={styles.catalogNotice}>
            Suggestions couldn't be loaded. You can still type your question once products are
            available.
          </p>
        )}
      </div>
      {notice}
    </div>
  );
}
